import { v4 as generateID } from '@lukeed/uuid';
import simpleClustering from 'ml-simple-clustering';
import { addMissingIDs } from '../peaks/util/addMissingIDs';
import jAnalyzer from '../signals/utils/jAnalyzer';
import { formatZones } from './util/formatZone';
const { parse, stringify } = JSON;
export function xyzJResAnalyzer(signals, options = {}) {
    const { reference = 0, getZones = false, referenceMaxShiftError = 0.08, tolerances = [10, 100], nuclei = ['1H', '1H'], observedFrequencies = [400, 400], jAnalyzer = {
        jAxisKey: { jAxis: 'y', intensity: 'z' },
    }, } = options;
    const temporalSignals = compilePattern(signals, {
        observedFrequencies,
        tolerances,
        nuclei,
        jAnalyzer,
    });
    //check if the signal are symmetric around the reference
    let signals2D = [];
    for (const tempSignal of temporalSignals) {
        const delta = tempSignal.y.delta;
        if (Math.abs(delta - reference) > referenceMaxShiftError)
            continue;
        signals2D.push(tempSignal);
    }
    signals2D = addMissingIDs(signals2D);
    return getZones ? formatZones(signals2D) : signals2D;
}
function compilePattern(signals, options) {
    const { observedFrequencies, tolerances, nuclei, jAnalyzer: jAnalyzerOptions, } = options;
    const signalOptions = {
        observedFrequencies,
        tolerances,
        nuclei,
        dx: signals[0].x.resolution,
        dy: signals[0].y.resolution,
    };
    const defaultWidth = 0.02 * observedFrequencies[0];
    const newSignals = [];
    //adapt to 1D jAnalyzer
    for (const signal of signals) {
        const peaks = [];
        for (const peak of signal.peaks || []) {
            peaks.push({
                ...peak,
                width: defaultWidth,
            });
        }
        peaks.sort((a, b) => a.y - b.y);
        newSignals.push({
            nbPeaks: peaks.length,
            multiplicity: '',
            pattern: '',
            startX: peaks[0].y - defaultWidth,
            stopX: peaks[peaks.length - 1].y + defaultWidth,
            delta: signal.y.delta,
            observe: observedFrequencies[1],
            integralData: {
                value: -1,
                from: Number.MAX_SAFE_INTEGER,
                to: Number.MIN_SAFE_INTEGER,
            },
            peaks,
        });
    }
    for (let i = 0; i < signals.length; i++) {
        const signal = jAnalyzer.compilePattern(newSignals[i], jAnalyzerOptions);
        if (signal.maskPattern &&
            signal.multiplicity !== 'm' &&
            signal.multiplicity !== '') {
            // Create a new signal with the removed peaks
            const peaksO = [];
            for (let j = signal.maskPattern.length - 1; j >= 0; j--) {
                if (!signal.maskPattern[j]) {
                    const peakR = signal.peaks.splice(j, 1)[0];
                    peaksO.push(peakR);
                    signal.mask.splice(j, 1);
                    signal.mask2?.splice(j, 1);
                    signal.maskPattern.splice(j, 1);
                    signal.nbPeaks--;
                }
            }
            if (peaksO.length > 0) {
                peaksO.reverse();
                const signals2D = createSignals2D(peaksO, signalOptions);
                for (const signal of signals2D) {
                    newSignals.push(signal);
                }
            }
        }
    }
    signals.sort((a, b) => {
        return b.x.delta - a.x.delta;
    });
    return signals;
}
function createSignals2D(peaksInput, options) {
    const { observedFrequencies, tolerances, nuclei, dx, dy } = options;
    const peaks = parse(stringify(peaksInput));
    const [nucleusX, nucleusY] = nuclei;
    const [toleranceX, toleranceY] = tolerances;
    const [observeFrequencyX, observeFrequencyY] = observedFrequencies;
    // The connectivity matrix is an square and symmetric matrix, so we'll only store the upper diagonal in an
    // array like form
    const connectivity = [];
    for (let i = 0; i < peaks.length; i++) {
        for (let j = i; j < peaks.length; j++) {
            if (Math.abs(peaks[i].x - peaks[j].x) * observeFrequencyX < toleranceX &&
                Math.abs(peaks[i].y - peaks[j].y) * observeFrequencyY < toleranceY) {
                // 24*24Hz We cannot distinguish peaks with less than 20 Hz of separation
                connectivity.push(1);
            }
            else {
                connectivity.push(0);
            }
        }
    }
    const clusters = simpleClustering(connectivity);
    const signals = [];
    if (clusters) {
        for (const cluster of clusters) {
            const signal = {
                id: generateID(),
                nucleusX,
                nucleusY,
                integralData: {
                    from: Number.MAX_SAFE_INTEGER,
                    to: Number.MIN_SAFE_INTEGER,
                },
                nbPeaks: 0,
                multiplicity: '',
                pattern: '',
                observe: observeFrequencyY,
                resolutionX: dx,
                resolutionY: dy,
                shiftX: 0,
                shiftY: 0,
            };
            const peaks2D = [];
            const minMax1 = [Number.MAX_VALUE, 0];
            const minMax2 = [Number.MAX_VALUE, 0];
            let sumZ = 0;
            for (let jPeak = cluster.length - 1; jPeak >= 0; jPeak--) {
                if (cluster[jPeak] === 1) {
                    signal.nbPeaks++;
                    if (!peaks[jPeak].width)
                        peaks[jPeak].width = 0.02;
                    peaks2D.push(peaks[jPeak]);
                    signal.shiftX += peaks[jPeak].x * peaks[jPeak].z;
                    signal.shiftY += peaks[jPeak].y * peaks[jPeak].z;
                    sumZ += peaks[jPeak].z;
                    if (peaks[jPeak].minX < minMax1[0]) {
                        minMax1[0] = peaks[jPeak].minX;
                    }
                    if (peaks[jPeak].maxX > minMax1[1]) {
                        minMax1[1] = peaks[jPeak].maxX;
                    }
                    if (peaks[jPeak].minY < minMax2[0]) {
                        minMax2[0] = peaks[jPeak].minY;
                    }
                    if (peaks[jPeak].maxY > minMax2[1]) {
                        minMax2[1] = peaks[jPeak].maxY;
                    }
                }
            }
            signal.fromTo = [
                { from: minMax1[0], to: minMax1[1] },
                { from: minMax2[0], to: minMax2[1] },
            ];
            signal.shiftX /= sumZ;
            signal.shiftY /= sumZ;
            signal.delta1 = signal.shiftY;
            signal.peaks = peaks2D;
            signals.push(signal);
        }
    }
    return signals;
}
//# sourceMappingURL=xyzJResAnalyzer.js.map